import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  effect,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import {
  FormBuilderService,
  IFormGroupConfig,
  UIButton,
  UIFilter,
  UIFormModule,
  UIIcon,
} from '@shared/ui';
import * as L from 'leaflet';
import 'leaflet.heat';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCheckboxModule } from 'ng-zorro-antd/checkbox';
import { NzDrawerModule } from 'ng-zorro-antd/drawer';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzSliderModule } from 'ng-zorro-antd/slider';
import { catchError, of } from 'rxjs';
import { parkingMapFilterFormConfig } from './configs/parking-map';
import {
  IParkingFilterParams,
  IParkingLocation,
  MapProviderTypeEnum,
  StatusEnum,
} from './models/parking-map';
import { MapService } from './services/map.service';
import { MarkersService } from './services/markers.service';
import { ParkingApiService } from './services/parking-api.service';
import { ParkingHistoriesHubService } from './services/parking-histories-hub.service';

@Component({
  selector: 'aggregator-parking-map',
  standalone: true,
  imports: [
    NzButtonModule,
    NzDrawerModule,
    UIFilter,
    UIFormModule,
    NzSelectModule,
    NzSliderModule,
    FormsModule,
    ReactiveFormsModule,
    NzCheckboxModule,
    UIIcon,
    NzModalModule,
    UIButton,
  ],
  templateUrl: './parking-map.component.html',
  styleUrl: './parking-map.component.scss',
  providers: [MarkersService, MapService],
})
export class ParkingMapComponent implements OnInit, AfterViewInit {
  selectedMapLayer: MapProviderTypeEnum = MapProviderTypeEnum.GIS2;
  isGrayscale = true;
  parkings: IParkingLocation[] = [];
  mapProviderTypeEnum = MapProviderTypeEnum;
  totalSpotsCounts = 0;
  freeSpotsRange = signal<[number, number]>([0, 100]); // Сигнал для диапазона
  isFilterVisible = false;

  parkingMapFilterForm?: FormGroup;
  parkingMapFilterFormConfig: IFormGroupConfig = parkingMapFilterFormConfig;

  private readonly destroyRef = inject(DestroyRef);
  private parkingService = inject(ParkingApiService);
  private mapService = inject(MapService);
  private parkingHistoriesHub = inject(ParkingHistoriesHubService);

  constructor(private formBuilder: FormBuilderService, private cdr: ChangeDetectorRef) {
    effect(() => {
      const range = this.freeSpotsRange();
      this.updateFreeSpotsRangeFilter(range);
    });
  }

  ngOnInit(): void {
    this.parkingMapFilterForm = this.formBuilder.build(this.parkingMapFilterFormConfig);
    this.initializeConnection();

    const savedFilter = localStorage.getItem('parkingFilter');

    if (savedFilter) {
      const filterValues = JSON.parse(savedFilter);
      this.parkingMapFilterForm.patchValue(filterValues, { emitEvent: false });

      const filter: Partial<IParkingFilterParams> = {
        categoryIds: filterValues?.categoryId ? [filterValues.categoryId] : [],
        operatorIds: filterValues?.operatorId ? [filterValues.operatorId] : [],
        typeIds: filterValues?.typeId ? [filterValues.typeId] : [],
        statuses: filterValues?.status ?? StatusEnum.All,
      };

      this.loadParkings(filter);
      this.updateFreeSpotsRangeFilter(this.freeSpotsRange());
    } else {
      this.loadParkings({
        categoryIds: [],
        operatorIds: [],
        typeIds: [],
        statuses: StatusEnum.All,
      });

      this.updateFreeSpotsRangeFilter(this.freeSpotsRange());
    }
  }

  get isFilterEmpty(): boolean {
    const values = this.parkingMapFilterForm?.value;

    return Boolean(
      values?.categoryId ||
        values?.operatorId ||
        values?.typeId ||
        (values?.status && values.status !== StatusEnum.All),
    );
  }

  applyFilter(): void {
    const values = this.parkingMapFilterForm?.value;

    const filter: Partial<IParkingFilterParams> = {
      categoryIds: values?.categoryId ? [values.categoryId] : [],
      operatorIds: values?.operatorId ? [values.operatorId] : [],
      typeIds: values?.typeId ? [values.typeId] : [],
      statuses: values?.status ?? StatusEnum.All,
    };

    localStorage.setItem('parkingFilter', JSON.stringify(values));

    this.loadParkings(filter);
    this.onFilterCollapsed();
    this.updateFreeSpotsRangeFilter(this.freeSpotsRange());
  }

  onFilterCollapsed(): void {
    this.isFilterVisible = !this.isFilterVisible;
    this.cdr.detectChanges();
  }

  updateFreeSpotsRangeFilter(range: [number, number]): void {
    this.freeSpotsRange.set(range);

    this.parkingMapFilterForm?.controls['freeSpotsRange']?.setValue(range, { emitEvent: false });

    const [min, max] = range;

    const parkingIds = this.parkings
      .filter(parking => {
        const freeSpots = parking.freeSpotsCount;
        return freeSpots >= min && freeSpots <= max;
      })
      .map(parking => parking.id);

    this.mapService.filterMarkers(parkingIds);
    this.mapService.refreshMap();
  }

  private loadParkings(filterParams: Partial<IParkingFilterParams>): void {
    this.parkingService
      .getParkingLocations(filterParams)
      .pipe(
        catchError(() => of({ value: null })),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: response => {
          if (response.value && response.value.items && response.value.items.length > 0) {
            this.parkings = response.value.items;
            this.totalSpotsCounts = Math.max(...this.parkings.map(item => item.totalSpots));
            this.mapService.clearMarkers();
            this.mapService.addMarkers(this.parkings);
          } else {
            this.parkings = [];
            this.mapService.clearMarkers();
            this.mapService.filterMarkers([]);
            this.mapService.refreshMap();
          }
        },
        error: () => {
          this.parkings = [];
          this.mapService.clearMarkers();
          this.mapService.filterMarkers([]);
          this.mapService.refreshMap();
        },
      });
  }

  private initializeConnection(): void {
    this.parkingHistoriesHub.initConnection();
    this.parkingHistoriesHub.parkingUpdates$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(update => {
        if (update.parkingId) {
          this.mapService.updateParkingOccupancy(update.parkingId, update.freeSpotsCount);
          // Перефильтровать маркеры при обновлении свободных мест
          // this.updateFreeSpotsRangeFilter(this.freeSpotsRange());
        }
      });
  }

  ngAfterViewInit(): void {
    this.mapService.initMap([51.131276, 71.418158], 15);
    this.mapService.setMapLayer(this.selectedMapLayer, this.isGrayscale);
  }

  changeMapLayer(layer: MapProviderTypeEnum): void {
    this.selectedMapLayer = layer;
    this.mapService.setMapLayer(layer, this.isGrayscale);
  }

  toggleMapColor(): void {
    this.isGrayscale = !this.isGrayscale;
    this.mapService.setMapLayer(this.selectedMapLayer, this.isGrayscale);
  }

  heatmapLayer?: ReturnType<typeof L.heatLayer>;
  isHeatmapMode = signal(false);

  toggleHeatmap(): void {
    this.isHeatmapMode.update(prev => !prev);

    if (this.isHeatmapMode()) {
      const heatData: [number, number, number][] = this.parkings.map(p => {
        const lat = p.location.y;
        const lng = p.location.x;

        const total = p.totalSpots || 1;
        const free = p.freeSpotsCount || 0;
        const loadRatio = (total - free) / total;

        return [lat, lng, loadRatio];
      });

      this.heatmapLayer = L.heatLayer(heatData, {
        radius: 21,
        blur: 5,
        maxZoom: 17,
        gradient: {
          0.0: '#49d638',
          0.52: '#ffce46',
          1.0: '#ff0000',
        },
      });

      this.heatmapLayer.addTo(this.mapService.mapInstance);
      this.mapService.clearMarkers();
    } else {
      if (this.heatmapLayer) {
        this.mapService.mapInstance.removeLayer(this.heatmapLayer);
      }

      this.mapService.clearMarkers();
      this.mapService.addMarkers(this.parkings);
    }
  }
}
